import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { Box, Button, CircularProgress } from '@mui/material';
import { useResizeRect } from '@realadvisor/observe';
import {
  type ScaleLinear,
  type ScalePoint,
  scaleLinear,
  scalePoint,
} from 'd3-scale';

import type { AppraisalGraphQuery } from '@graphql/__generated__/graphql';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { usePropertyAppraisal } from '@modules/_shared/hooks/useAppraiseProperty';
import { useLocale } from '@shared/hooks/locale';
import { useTheme } from '@shared/theme/theme';

type AppraisalData = {
  name: string;
  min?: number | null;
  value?: number | null;
  max?: number | null;
};

type LatestAppraisal = Omit<AppraisalData, 'name'>;

type AppraisalDisplay = {
  x: ScalePoint<string>;
  y: ScaleLinear<number, number>;
  item: AppraisalData;
};

const formatValue = (value: number, locale: string) => {
  if (value < 1_000) {
    return value.toLocaleString(locale, {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    });
  }
  if (value < 1_000_000) {
    return (
      (value / 1_000).toLocaleString(locale, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      }) + 'K'
    );
  }
  return (
    (value / 1_000_000).toLocaleString(locale, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }) + 'M'
  );
  // TODO: use code below when target Safari version become 14.1+
  // const formatValue = (value, locale) => {
  //   return new Intl.NumberFormat(locale, { notation: 'compact' }).format(value);
  // };
};

const getModelData = (
  appraisal: NonNullable<AppraisalGraphQuery['appraisals_by_pk']>,
): AppraisalData[] => {
  const sortedValuations = [...appraisal.valuations].sort((a, b) => {
    const aIsRealadvisor = a.model.toLowerCase().startsWith('realadvisor');
    const bIsRealadvisor = b.model.toLowerCase().startsWith('realadvisor');

    if (aIsRealadvisor && !bIsRealadvisor) {
      return -1;
    }
    return a.model.localeCompare(b.model);
  });

  return sortedValuations.reduce<AppraisalData[]>((acc, valuation) => {
    const { min, value, max, valuation_model } = valuation;

    if (
      (min != null || value != null || max != null) &&
      valuation_model.short_name
    ) {
      acc.push({
        name: valuation_model.short_name,
        min,
        value,
        max,
      });
    }

    return acc;
  }, []);
};

const AppraisalBound = ({
  x,
  y,
  name,
  value,
}: {
  x: ScalePoint<string>;
  y: ScaleLinear<number, number>;
  name: string;
  value: number | null | undefined;
}) => {
  const { colors, text } = useTheme();
  const { locale } = useLocale();

  if (value == null) {
    return null;
  }

  return (
    <>
      <circle
        cx={x(name)}
        cy={y(value)}
        r={3}
        strokeWidth="2"
        style={{ fill: colors.white, stroke: colors.blue400 }}
      />
      <text
        x={x(name)}
        dx={20}
        y={y(value)}
        style={{ fontSize: 10, fontWeight: text.font.regular }}
        textAnchor="middle"
      >
        {formatValue(value, locale)}
      </text>
    </>
  );
};

const AppraisalValue = ({ x, y, item }: AppraisalDisplay) => {
  const { colors, text } = useTheme();
  const { locale } = useLocale();

  if (item.value != null) {
    const name = item.name;
    const value = item.value;
    return (
      <>
        <circle
          cx={x(name)}
          cy={y(value)}
          r={5}
          style={{ fill: colors.blue400 }}
        />
        <text
          x={x(name)}
          dx={20}
          y={y(value)}
          style={{ fontSize: 12, fontWeight: text.font.medium }}
          textAnchor="middle"
        >
          {formatValue(value, locale)}
        </text>
      </>
    );
  }

  return null;
};

const getAppraisalDomain = (
  appraisalItems: AppraisalData[],
): [number, number] => {
  const values = appraisalItems
    .flatMap(item => [item.min, item.value, item.max])
    .filter((value): value is number => value != null);

  if (values.length === 0) {
    return [0, 0]; // Default domain if no valid values
  }

  return [Math.min(...values), Math.max(...values)];
};

const getArrayItem = <T extends any>(
  array: ReadonlyArray<T>,
  index: number,
): T => array[index];

const Chart = ({
  width,
  height,
  data,
  latestAppraisal,
}: {
  width: number;
  height: number;
  data: AppraisalData[];
  latestAppraisal: LatestAppraisal;
}) => {
  const { colors, text } = useTheme();
  const { locale } = useLocale();
  const x = scalePoint()
    .domain(data.map(item => item.name))
    .range([0, width])
    .padding(1);
  const y = scaleLinear()
    .domain(getAppraisalDomain(data))
    .range([height - 30, 20])
    .nice(5);

  const ticks = y.ticks(5);
  const xStart = x(getArrayItem(data, 0)?.name);
  const xEnd = x(getArrayItem(data, data.length - 1)?.name);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      // Text elements inherit this fill so labels follow the theme. Set via
      // `style` because CSS var() does not resolve in SVG presentation attrs.
      style={{ fill: colors.black }}
    >
      <g transform={`translate(0, ${height - 30})`}>
        {data.map(item => {
          const pos = x(item.name);
          return (
            <React.Fragment key={item.name}>
              <line
                x1={pos}
                x2={pos}
                y1={0}
                y2={6}
                style={{ stroke: colors.grey600 }}
              />
              <text
                x={pos}
                y="16"
                dy="0.71em"
                textAnchor="middle"
                style={{ fontSize: 12, fontWeight: text.font.medium }}
              >
                {item.name}
              </text>
            </React.Fragment>
          );
        })}
      </g>

      <g transform={`translate(34, 0)`}>
        {ticks.map(tick => (
          <React.Fragment key={tick}>
            <line
              x1={0}
              x2={width}
              y1={y(tick)}
              y2={y(tick)}
              strokeWidth={2}
              strokeDasharray="3 6"
              style={{ stroke: colors.divider }}
            />
            <text
              x={0}
              y={y(tick)}
              textAnchor="end"
              style={{ fontSize: 10, fontWeight: text.font.medium }}
            >
              {formatValue(tick, locale)}
            </text>
          </React.Fragment>
        ))}
      </g>

      <line
        x1={xStart}
        x2={xEnd}
        y1={y(latestAppraisal.max ?? 0)}
        y2={y(latestAppraisal.max ?? 0)}
        strokeWidth="1"
        strokeDasharray="2 2"
        style={{ stroke: colors.green600 }}
      />

      <line
        x1={xStart}
        x2={xEnd}
        y1={y(latestAppraisal.value ?? 0)}
        y2={y(latestAppraisal.value ?? 0)}
        strokeWidth="2"
        strokeDasharray="2 2"
        style={{ stroke: colors.green600 }}
      />

      <line
        x1={xStart}
        x2={xEnd}
        y1={y(latestAppraisal.min ?? 0)}
        y2={y(latestAppraisal.min ?? 0)}
        strokeWidth="1"
        strokeDasharray="2 2"
        style={{ stroke: colors.green500 }}
      />

      {data.map(item => (
        <AppraisalBound
          key={`max-${item.name}`}
          x={x}
          y={y}
          name={item.name}
          value={item.max}
        />
      ))}

      {data.map(item => (
        <AppraisalValue key={item.name} x={x} y={y} item={item} />
      ))}

      {data.map(item => (
        <AppraisalBound
          key={`min-${item.name}`}
          x={x}
          y={y}
          name={item.name}
          value={item.min}
        />
      ))}
    </svg>
  );
};

const ResizableChart: React.FC<{
  chartData: AppraisalData[];
  targetRef: React.RefObject<HTMLDivElement | null>;
  latestAppraisal: LatestAppraisal;
}> = ({ chartData, targetRef, latestAppraisal }) => {
  const targetRect = useResizeRect(targetRef);

  if (!targetRect) {
    return null;
  }

  return (
    <Chart
      width={targetRect.width}
      height={targetRect.height}
      data={chartData}
      latestAppraisal={latestAppraisal}
    />
  );
};

type AppraisalGraphProps = {
  latestAppraisalId?: string;
  propertyId: string;
  canAppraise?: boolean;
};

export const AppraisalGraph: React.FC<AppraisalGraphProps> = ({
  latestAppraisalId,
  propertyId,
  canAppraise = true,
}) => {
  const { t } = useLocale();
  const targetRef = useRef<HTMLDivElement>(null);
  const [currentError, setCurrentError] = useState<ErrorLike | null>(null);

  const {
    appraiseProperty,
    appraisalData,
    queryLoading,
    mutationLoading,
    queryError,
    mutationError,
  } = usePropertyAppraisal(latestAppraisalId);

  useEffect(() => {
    if (mutationError || queryError) {
      setCurrentError(mutationError || queryError || null);
    }
  }, [mutationError, queryError]);

  const handleClickAppraise = useCallback(async () => {
    if (!canAppraise) {
      return;
    }

    try {
      await appraiseProperty(propertyId);
    } catch (error) {
      console.error('Error in appraiseProperty:', error);
    }
  }, [appraiseProperty, canAppraise, propertyId]);

  if (queryLoading) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 3,
        }}
      >
        <CircularProgress disableShrink />
      </Box>
    );
  }

  const chartData = appraisalData ? getModelData(appraisalData) : [];

  const latestAppraisal = {
    min: appraisalData?.min,
    value: appraisalData?.value,
    max: appraisalData?.max,
  };

  return (
    <>
      {!!chartData.length && (
        <Box>
          <div css={{ height: 280, overflow: 'hidden' }} ref={targetRef}>
            <ResizableChart
              chartData={chartData}
              targetRef={targetRef}
              latestAppraisal={latestAppraisal}
            />
          </div>
        </Box>
      )}
      {canAppraise ? (
        <Button
          variant="outlined"
          loading={mutationLoading}
          fullWidth
          onClick={handleClickAppraise}
          disabled={!propertyId}
          loadingPosition="start"
          sx={{ mt: 2 }}
        >
          {t('Appraise')}
        </Button>
      ) : null}

      <MutationErrorSnackbar
        error={currentError}
        onClose={() => setCurrentError(null)}
      />
    </>
  );
};

export default AppraisalGraph;
